/**
 * Hooks for the Hired Candidates ledger.
 *
 *   useHiredLedger()      — GET  /api/t/{slug}/outcomes/hired
 *   useLedgerCellPatch()  — PATCH /api/t/{slug}/candidates/{id}/outcome
 *
 * ── Why a different endpoint from `useOutcomes` ──────────────────────────────
 * `GET /outcomes` is driven by ``candidate_outcomes`` rows, so a candidate moved
 * to Hired with nothing recorded yet did not appear at all — which is why the
 * Outcomes page read blank. `GET /outcomes/hired` is driven by candidates in a
 * hired state LEFT JOIN their outcome, so every hire shows up with empty,
 * fillable cells and `outcome_id: null`. Rows are keyed on `candidate_id`;
 * `outcome_id` is not a usable key here.
 *
 * ── Server-side, not client-side ─────────────────────────────────────────────
 * search / sort / needs_data all go to the server and are part of the query key.
 * The hired population of a real agency is far too large to filter in the
 * browser, and filtering a single fetched page would quietly lie about what is
 * in the tenant.
 *
 * Scoping is enforced ON THE SERVER (PP-2) exactly as before: recruiter → own
 * candidates, hiring-manager → assigned-JD candidates, admin/SA → all in-tenant.
 * The UI cannot widen it; it only narrows further via the admin-only `?owner=`.
 */
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from "@tanstack/react-query";
import { useParams } from "react-router-dom";
import { ApiError } from "@/lib/apiError";
import { apiClient } from "@/lib/apiClient";
import type { Verdict } from "@/hooks/queries/outcomes/useOutcome";
import {
  LEDGER_FIELDS,
  type LedgerFieldKey,
} from "@/components/v2/outcomes/ledgerFields";

/** Collapsed realized outcome (backend ``realized_outcome`` service). */
type RealizedState = "success" | "failure" | "pending";

/** Server-side sort keys — mirrors backend ``HiredLedgerSort``. */
export type HiredLedgerSort =
  | "hired"
  | "candidate"
  | "role"
  | "completeness"
  | "submitted_to_client_at"
  | "client_callback_at"
  | "offer_extended_at"
  | "placed_at"
  | "hired_at"
  | "start_date"
  | "updated_at";

export type SortDirection = "asc" | "desc";

/** One ledger row — mirrors backend ``HiredLedgerItem``. */
export interface HiredLedgerItem {
  candidate_id: string;
  candidate_name: string;
  /** Null for a hired candidate with no outcome row yet. Never a row key. */
  outcome_id: string | null;
  /**
   * The PATCH target: the JD the stored outcome row is keyed to, else the hired
   * pairing's JD. Send it back on every cell edit so the write lands on THIS row
   * instead of forking a sibling.
   */
  jd_id: string | null;
  role: string | null;

  // The 11 editable analytics variables.
  submitted_to_client_at: string | null;
  client_callback_at: string | null;
  offer_extended_at: string | null;
  placed_at: string | null;
  placement_failed_reason: string | null;
  hired_at: string | null;
  start_date: string | null;
  still_employed_at_90d: boolean | null;
  still_employed_at_180d: boolean | null;
  performance_rating_at_180d: string | null;
  would_rehire: boolean | null;

  // Read-only prediction vs. realized.
  predicted_fit_score: number | null;
  verdict: Verdict | null;
  realized: RealizedState;

  // Worklist.
  completeness: number;
  completeness_total: number;

  owner_user_id: string | null;
  updated_at: string | null;
}

interface HiredLedgerPage {
  items: HiredLedgerItem[];
  next_cursor: string | null;
  has_more: boolean;
}

interface UseHiredLedgerOpts {
  /** Admin-only owner filter (`?owner=`). */
  owner?: string | null;
  search?: string;
  sort?: HiredLedgerSort;
  direction?: SortDirection;
  needsData?: boolean;
  /** Page size (backend clamps 1–100; default 25). */
  limit?: number;
}

/** Cache-key prefix — the patch hook sweeps every page under it. */
function hiredLedgerKeyPrefix(tenantSlug: string | undefined): unknown[] {
  return ["outcomes", "hired", tenantSlug];
}

export function useHiredLedger(
  opts: UseHiredLedgerOpts = {},
): UseQueryResult<HiredLedgerPage, ApiError> {
  const { tenantSlug } = useParams<{ tenantSlug: string }>();
  const owner = opts.owner ?? null;
  const search = opts.search?.trim() ?? "";
  const sort = opts.sort ?? "hired";
  const direction = opts.direction ?? "desc";
  const needsData = opts.needsData ?? false;
  const limit = opts.limit ?? 100;

  return useQuery<HiredLedgerPage, ApiError>({
    queryKey: [
      ...hiredLedgerKeyPrefix(tenantSlug),
      { owner, search, sort, direction, needsData, limit },
    ],
    queryFn: async () => {
      const params: Record<string, string> = {
        limit: String(limit),
        sort,
        direction,
      };
      if (owner) params.owner = owner;
      if (search) params.search = search;
      if (needsData) params.needs_data = "true";
      const r = await apiClient.get<HiredLedgerPage>(
        `/api/t/${encodeURIComponent(tenantSlug!)}/outcomes/hired`,
        { params },
      );
      return r.data;
    },
    enabled: !!tenantSlug,
    staleTime: 30_000,
    // Keep the previous page on screen while a new search/sort resolves, so
    // typing in the search box does not flash the whole grid to a skeleton.
    placeholderData: (prev) => prev,
  });
}

// ── Inline cell edit ──────────────────────────────────────────────────────────

/**
 * PATCH body for one cell. Exactly one of the 11 keys, plus the row's `jd_id`.
 *
 * A `null` value is meaningful and MUST be sent: the backend keys on key
 * presence, so `{"placed_at": null}` clears the cell while omitting the key
 * leaves it untouched. Never strip nulls from this body.
 */
interface LedgerCellPatch {
  candidateId: string;
  jdId: string | null;
  field: LedgerFieldKey;
  value: string | boolean | null;
}

interface PatchContext {
  /** Every cached ledger page, so a failure can put all of them back. */
  previous: Array<[readonly unknown[], HiredLedgerPage | undefined]>;
}

/**
 * Re-derive the optimistic completeness NUMERATOR only.
 *
 * The numerator is simply "how many of the variables hold a value", the same
 * rule the server uses, so it is safe to recompute here — and it reads off
 * `LEDGER_FIELDS` rather than a hand-copied list, because a third copy of the
 * field set is exactly how these drift apart.
 *
 * The DENOMINATOR is deliberately NOT recomputed. It depends on the tenant's
 * persona and on whether the placement failed, and duplicating that rule in
 * TypeScript would leave two definitions of "complete" free to disagree —
 * which is the bug this change exists to fix. The server owns it; the
 * `onSettled` invalidation re-syncs it a moment later.
 *
 * One edit can legitimately move the denominator (recording
 * `still_employed_at_180d: false` makes `placement_failed_reason` askable, so
 * the total goes up by one). Until the refetch lands the row may read one field
 * short of the truth; the clamp below only guarantees it never paints an
 * impossible fraction like 8/7 in the meantime.
 */
function withCompleteness(row: HiredLedgerItem): HiredLedgerItem {
  const filled = LEDGER_FIELDS.filter((f) => {
    const v = row[f.key];
    return v !== null && v !== undefined;
  }).length;
  return {
    ...row,
    completeness: filled,
    completeness_total: Math.max(row.completeness_total, filled),
  };
}

/**
 * Optimistic single-cell edit with rollback.
 *
 * The cell paints the new value immediately (typing a date and watching a
 * spinner for 300ms is what made the old form feel like paperwork). On failure
 * EVERY cached page is restored and the error is surfaced by the caller — an
 * edit is never silently dropped.
 */
export function useLedgerCellPatch(): UseMutationResult<
  unknown,
  ApiError,
  LedgerCellPatch,
  PatchContext
> {
  const { tenantSlug } = useParams<{ tenantSlug: string }>();
  const queryClient = useQueryClient();
  const prefix = hiredLedgerKeyPrefix(tenantSlug);

  return useMutation<unknown, ApiError, LedgerCellPatch, PatchContext>({
    mutationFn: async ({ candidateId, jdId, field, value }) => {
      const body: Record<string, unknown> = { [field]: value };
      // jd_id addresses the row; it is not itself an outcome field, so it never
      // satisfies the backend's at-least-one-field check on its own.
      if (jdId) body.jd_id = jdId;
      const r = await apiClient.patch(
        `/api/t/${encodeURIComponent(tenantSlug!)}/candidates/${encodeURIComponent(
          candidateId,
        )}/outcome`,
        body,
      );
      return r.data;
    },

    onMutate: async ({ candidateId, field, value }) => {
      await queryClient.cancelQueries({ queryKey: prefix });
      const previous = queryClient.getQueriesData<HiredLedgerPage>({ queryKey: prefix });

      queryClient.setQueriesData<HiredLedgerPage>({ queryKey: prefix }, (page) => {
        if (!page) return page;
        return {
          ...page,
          items: page.items.map((row) =>
            row.candidate_id === candidateId
              ? withCompleteness({ ...row, [field]: value } as HiredLedgerItem)
              : row,
          ),
        };
      });

      return { previous };
    },

    onError: (_err, _vars, ctx) => {
      // Put every page back exactly as it was. Partial rollback would leave the
      // grid showing a value the server never accepted.
      ctx?.previous.forEach(([key, data]) => {
        queryClient.setQueryData(key, data);
      });
    },

    onSettled: () => {
      // Re-sync against the server: it recomputes `verdict` and `realized` from
      // the terminal fields, which the optimistic patch cannot derive.
      void queryClient.invalidateQueries({ queryKey: prefix });
      void queryClient.invalidateQueries({ queryKey: ["outcome", tenantSlug] });
    },
  });
}
